import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SparkMD5 from 'spark-md5';
import { getErrorMessage } from '@billda/shared-ui';
import { useSnackbarStore } from '../../../../shared/stores/snackbarStore';
import {
  requestVersionUploadUrl,
  confirmVersionUpload,
  uploadFileToBlobWithProgress,
} from '../../api/filesApi';

type UploadState = 'idle' | 'fileSelected' | 'uploading';

interface VersionUploadAreaProps {
  fileId: string;
  contentType: string;
  onUploadComplete: () => void;
}

export function VersionUploadArea({
  fileId,
  contentType,
  onUploadComplete,
}: VersionUploadAreaProps) {
  const showSnackbar = useSnackbarStore(s => s.showSnackbar);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<UploadState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [progress, setProgress] = useState(0);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const resetState = () => {
    setState('idle');
    setSelectedFile(null);
    setNotes('');
    setProgress(0);
    setInlineError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setState('fileSelected');
    setInlineError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      return;
    }

    setState('uploading');
    setProgress(0);
    setInlineError(null);

    try {
      // Compute MD5 hash for duplicate detection (matches Azure Blob Content-MD5)
      const buffer = await selectedFile.arrayBuffer();
      const contentHash = btoa(SparkMD5.ArrayBuffer.hash(buffer, true));

      const { uploadUrl, versionId } = await requestVersionUploadUrl(fileId, {
        contentType: selectedFile.type,
        fileSizeBytes: selectedFile.size,
        notes: notes || undefined,
        contentHash,
      });

      await uploadFileToBlobWithProgress(
        uploadUrl,
        selectedFile,
        selectedFile.type,
        (loaded, total) => {
          setProgress(Math.round((loaded / total) * 100));
        }
      );

      await confirmVersionUpload(fileId, { versionId });

      showSnackbar('New version uploaded', 'success');
      resetState();
      onUploadComplete();
    } catch (err: unknown) {
      const message = getErrorMessage(err);

      // Check for specific 409 errors
      const isAxiosError =
        err !== null &&
        typeof err === 'object' &&
        'response' in err &&
        typeof (err as { response?: unknown }).response === 'object';

      if (isAxiosError) {
        const response = (err as { response: { status: number } }).response;
        if (response.status === 409) {
          if (message.includes('identical')) {
            showSnackbar(
              'This file is identical to an existing version',
              'error'
            );
          } else {
            setInlineError(
              'A version upload is already in progress. Please wait and try again.'
            );
          }
          setState('idle');
          setSelectedFile(null);
          setNotes('');
          setProgress(0);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          return;
        }
      }

      showSnackbar(message, 'error');
      setState('fileSelected');
    }
  };

  return (
    <Box data-testid="upload-new-version">
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept={contentType}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) {
            handleFileSelect(file);
          }
        }}
      />

      {inlineError && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {inlineError}
        </Alert>
      )}

      {state === 'idle' && (
        <Box
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          sx={{
            border: 2,
            borderStyle: 'dashed',
            borderColor: 'divider',
            borderRadius: 2,
            py: 3,
            px: 2,
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CloudUploadOutlinedIcon
            sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }}
          />
          <Typography variant="body2" fontWeight={500}>
            Upload new version
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Drop a file here or click to browse
          </Typography>
        </Box>
      )}

      {state === 'fileSelected' && selectedFile && (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {selectedFile.name}
          </Typography>
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
            sx={{ mb: 1.5 }}
            data-testid="version-notes-input"
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleUpload}
              data-testid="confirm-version-upload"
            >
              Upload
            </Button>
            <Link
              component="button"
              variant="body2"
              onClick={resetState}
              sx={{ cursor: 'pointer' }}
            >
              Cancel
            </Link>
          </Box>
        </Box>
      )}

      {state === 'uploading' && selectedFile && (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {selectedFile.name}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mb: 0.5 }}
          />
          <Typography variant="caption" color="text.secondary">
            Uploading... {progress}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}
